/**
 * Typed localStorage wrapper with an in-memory fallback — the storage core the
 * Gun layer persists its small hot-path values through (device id, alias,
 * encrypted pair, remember-me).
 *
 * Lives INSIDE the layer so the folder is self-contained when lifted; the app's
 * own storage module (`src/shared/utils/storage.ts`) builds on this same core,
 * so there is exactly one implementation of the quota-fallback behaviour.
 */

const memoryStorage = new Map<string, string>();

function hasLocalStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined' && !!localStorage;
  } catch {
    return false;
  }
}

function readRaw(key: string): string | null {
  try {
    if (hasLocalStorage()) {
      const raw = localStorage.getItem(key);
      if (raw !== null) return raw;
    }
  } catch {
    // Fall back below.
  }
  return memoryStorage.has(key) ? memoryStorage.get(key)! : null;
}

/**
 * True once we have signaled that persistent storage writes are failing.
 * The signal fires once so listeners can warn the user without being spammed.
 */
let quotaSignaled = false;

function signalQuotaFailure(key: string, error: unknown): void {
  if (quotaSignaled) return;
  quotaSignaled = true;
  console.error(
    `[storage] localStorage write failed (key: ${key}); falling back to in-memory storage. ` +
    'Data written from now on will NOT survive a reload.',
    error
  );
  try {
    window.dispatchEvent(new CustomEvent('tt-storage-quota', { detail: { key } }));
  } catch {
    // Non-browser environment; console signal above is enough.
  }
}

function writeRaw(key: string, value: string): void {
  let stored = false;
  try {
    if (hasLocalStorage()) {
      localStorage.setItem(key, value);
      stored = true;
    }
  } catch (error) {
    // Quota exceeded or storage unavailable: fall back below, but signal once
    // so the UI can warn about non-persistent data.
    signalQuotaFailure(key, error);
  }

  if (!stored) {
    memoryStorage.set(key, value);
  }
}

function removeRaw(key: string): void {
  try {
    if (hasLocalStorage()) {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore and continue.
  }
  memoryStorage.delete(key);
}

function listKeys(): string[] {
  try {
    if (hasLocalStorage()) {
      return Object.keys(localStorage);
    }
  } catch {
    // Fall back below.
  }
  return Array.from(memoryStorage.keys());
}

export const localStore = {
  /** Get a JSON value; `defaultValue` when absent or unparseable. */
  get<T>(key: string, defaultValue: T): T {
    try {
      const raw = readRaw(key);
      if (raw === null) return defaultValue;
      const parsed = JSON.parse(raw) as T;
      return parsed ?? defaultValue;
    } catch {
      return defaultValue;
    }
  },

  /** Set a JSON value. Silently falls back to memory when storage is full. */
  set<T>(key: string, value: T): void {
    try {
      writeRaw(key, JSON.stringify(value));
    } catch {
      // Silently ignore storage errors (quota exceeded, etc.)
    }
  },

  remove(key: string): void {
    try {
      removeRaw(key);
    } catch {
      // Silently ignore
    }
  },

  /** Remove every key starting with one of `prefixes`. */
  clear(prefixes: string[]): void {
    try {
      for (const key of listKeys()) {
        if (prefixes.some((prefix) => key.startsWith(prefix))) {
          removeRaw(key);
        }
      }
    } catch {
      // Silently ignore
    }
  },

  /** The known storage keys, for cleanup and diagnostics. */
  keys(): string[] {
    return listKeys();
  },
};
